/**
 * SSO — the hub as the family's sign-in front door.
 *
 * Four panels, all real: the relying parties registered (FinSetu, TalentSetu, …), the email
 * domains that resolve to this organisation, the people who can sign in here (with what they are
 * entitled to), and the issuer facts a relying party needs. Every action is a mutation on the API;
 * every refusal is shown with its reason. A client secret is shown once, at creation, and never
 * again — the API does not keep it either.
 */

import { useState } from "react";
import { api, useApi, ApiError } from "../lib/api";
import { Card, Pill } from "../components";

interface Client { id: string; clientId: string; name: string; appKey: string; redirectUris: string[] }
interface Grant { id: string; clientId: string; appRole: string; appTenant: string | null }
interface Login {
  id: string; email: string; hasPasscode: boolean; lastLoginAt: string | null; lockedUntil: string | null;
  identity: { id: string; displayName: string; status: string; lifecycleState: string; hubRole: string }; grants: Grant[];
}
interface DomainRow { domain: string; verified: boolean; verifiedAt: string | null; proof: { type: "TXT"; name: string; value: string } | null }
/** What the last Verify said for a domain: the API's reason code and sentence, kept until the next attempt. */
interface Attempt { failed: boolean; code?: string; message?: string }
interface Describe { issuer: string; upstream: string; credential: string; live: boolean }

/** The hub's own role set — mirrored from apps/api/src/services/sso-roles.ts, which is the source of truth and refuses anything else. */
const HUB_ROLES = ["OWNER", "ADMIN", "MEMBER"] as const;

const input: React.CSSProperties = { padding: 8, background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 6, color: "var(--text)" };
const btn: React.CSSProperties = { padding: "8px 14px", background: "var(--accent)", border: 0, borderRadius: 6, color: "#fff", cursor: "pointer" };

function problemText(e: unknown): string {
  return e instanceof ApiError ? `${e.message}${e.hint ? ` — ${e.hint}` : ""}` : String(e);
}

export function Sso() {
  const describe = useApi<Describe>("/v1/sso/describe");
  const clients = useApi<{ clients: Client[] }>("/v1/sso/clients");
  const domains = useApi<{ domains: DomainRow[] }>("/v1/sso/domains");
  const logins = useApi<{ logins: Login[] }>("/v1/sso/logins");
  const [problem, setProblem] = useState<string | null>(null);
  const [secretShown, setSecretShown] = useState<{ clientId: string; secret: string } | null>(null);

  const [domain, setDomain] = useState("");
  const [attempts, setAttempts] = useState<Record<string, Attempt>>({});
  const [checking, setChecking] = useState<string | null>(null);
  const [person, setPerson] = useState({ displayName: "", email: "", passcode: "" });
  const [grant, setGrant] = useState({ identityId: "", clientId: "", appRole: "", appTenant: "" });
  const [client, setClient] = useState({ clientId: "", name: "", appKey: "", redirectUris: "" });

  const reloadAll = () => { clients.reload(); domains.reload(); logins.reload(); };
  const run = async (work: () => Promise<void>) => { setProblem(null); try { await work(); reloadAll(); } catch (e) { setProblem(problemText(e)); } };

  const verify = async (d: string) => {
    setChecking(d);
    try {
      await api(`/v1/sso/domains/${encodeURIComponent(d)}/verify`, { method: "POST", body: "{}" });
      setAttempts((a) => ({ ...a, [d]: { failed: false } }));
    } catch (e) {
      setAttempts((a) => ({ ...a, [d]: e instanceof ApiError ? { failed: true, code: e.code, message: `${e.message}${e.hint ? ` — ${e.hint}` : ""}` } : { failed: true, message: String(e) } }));
    } finally { setChecking(null); domains.reload(); }
  };

  return (
    <>
      <Card title="Issuer">
        {describe.data ? (
          <div className="muted" style={{ fontSize: 13, display: "grid", gap: 4 }}>
            <div>Issuer <code>{describe.data.issuer}</code> · discovery <code>{describe.data.issuer}/.well-known/openid-configuration</code></div>
            <div>Credential: {describe.data.credential}</div>
            <div>Upstream IdP: {describe.data.upstream}</div>
            <div>Relying parties exchange an authorization code (PKCE S256) for an RS256 ID token carrying <code>hub_tenant</code>, <code>app</code>, <code>app_role</code> and <code>app_tenant</code>. No grant, no token; a leaver is refused before any credential is checked.</div>
          </div>
        ) : <p className="muted">Loading…</p>}
      </Card>

      {problem && <p className="err" style={{ margin: "8px 0" }}>{problem}</p>}
      {secretShown && (
        <Card title={`Client secret for ${secretShown.clientId}`}>
          <p className="err">Shown once. Put it on the relying party now; it is not stored here.</p>
          <code style={{ wordBreak: "break-all" }}>{secretShown.secret}</code>
          <div style={{ marginTop: 8 }}><button style={btn} onClick={() => setSecretShown(null)}>I have stored it</button></div>
        </Card>
      )}

      <Card title="Relying parties">
        <p className="muted">Applications that send people here to sign in. Redirect URIs match exactly.</p>
        {clients.error && <p className="err">{problemText(clients.error)}</p>}
        <table>
          <thead><tr><th>Client</th><th>App</th><th>Redirect URIs</th></tr></thead>
          <tbody>
            {(clients.data?.clients ?? []).map((c) => (
              <tr key={c.id}><td><strong>{c.name}</strong><div className="muted">{c.clientId}</div></td><td>{c.appKey}</td><td>{c.redirectUris.map((u) => <div key={u}><code>{u}</code></div>)}</td></tr>
            ))}
          </tbody>
        </table>
        <form style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 }} onSubmit={(e) => { e.preventDefault(); void run(async () => {
          const res = await api<{ clientId: string; secret: string }>("/v1/sso/clients", { method: "POST", body: JSON.stringify({ ...client, redirectUris: client.redirectUris.split(/\s+/).filter(Boolean) }) });
          setSecretShown({ clientId: res.clientId, secret: res.secret }); setClient({ clientId: "", name: "", appKey: "", redirectUris: "" });
        }); }}>
          <input style={{ ...input, width: 150 }} placeholder="client_id" value={client.clientId} onChange={(e) => setClient({ ...client, clientId: e.target.value })} required />
          <input style={{ ...input, width: 150 }} placeholder="name" value={client.name} onChange={(e) => setClient({ ...client, name: e.target.value })} required />
          <input style={{ ...input, width: 120 }} placeholder="app key" value={client.appKey} onChange={(e) => setClient({ ...client, appKey: e.target.value })} required />
          <input style={{ ...input, flex: 1 }} placeholder="redirect URIs (space-separated)" value={client.redirectUris} onChange={(e) => setClient({ ...client, redirectUris: e.target.value })} required />
          <button style={btn} type="submit">Register relying party</button>
        </form>
        <p className="muted" style={{ fontSize: 12, marginTop: 6 }}>Re-registering an existing client_id rotates its secret.</p>
      </Card>

      <Card title="Email domains">
        <p className="muted">Sign-in finds the organisation from the email's domain. A domain you claim routes nobody until you prove you control it: publish the DNS record shown, then verify.</p>
        {domains.error && <p className="err">{problemText(domains.error)}</p>}
        {domains.data && domains.data.domains.length === 0 && <span className="muted">No domain yet — nobody can sign in until one is registered and verified.</span>}
        <div style={{ display: "grid", gap: 12 }}>
          {(domains.data?.domains ?? []).map((d) => {
            const attempt = attempts[d.domain];
            const state = d.verified ? "verified" : attempt?.failed ? "failed" : "claimed";
            return (
              <div key={d.domain} data-testid="domain-row" data-state={state} style={{ border: "1px solid var(--border)", borderRadius: 6, padding: 10 }}>
                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  <strong>{d.domain}</strong>
                  {state === "verified" && <Pill tone="ok">verified</Pill>}
                  {state === "claimed" && <Pill tone="warn">claimed — not verified</Pill>}
                  {state === "failed" && <Pill tone="bad">verification failed{attempt?.code ? ` · ${attempt.code}` : ""}</Pill>}
                </div>
                {state === "failed" && attempt?.message && <p className="err" data-testid="domain-failure" style={{ margin: "6px 0" }}>{attempt.message}</p>}
                {d.proof && (
                  <div style={{ marginTop: 8, display: "grid", gap: 4, fontSize: 13 }}>
                    <div className="muted">Publish this record at your DNS host, wait for it to propagate, then verify.</div>
                    <div>Type <code>{d.proof.type}</code></div>
                    <div>Name <code data-testid="proof-name" style={{ wordBreak: "break-all" }}>{d.proof.name}</code></div>
                    <div>Value <code data-testid="proof-value" style={{ wordBreak: "break-all" }}>{d.proof.value}</code></div>
                    <div><button style={btn} disabled={checking === d.domain} onClick={() => void verify(d.domain)}>{checking === d.domain ? "Checking DNS…" : "Verify domain"}</button></div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <form style={{ display: "flex", gap: 8, marginTop: 12 }} onSubmit={(e) => { e.preventDefault(); void run(async () => { await api("/v1/sso/domains", { method: "POST", body: JSON.stringify({ domain }) }); setDomain(""); }); }}>
          <input style={{ ...input, width: 240 }} placeholder="company.com" aria-label="Domain to claim" value={domain} onChange={(e) => setDomain(e.target.value)} required />
          <button style={btn} type="submit">Claim domain</button>
        </form>
      </Card>

      <Card title="People who sign in here">
        <p className="muted">Each person is an Identity with a hub login. Their hub role is what they may administer here (only an owner can change it, and not their own); what they may open, and as what, is a grant per relying party.</p>
        {logins.error && <p className="err">{problemText(logins.error)}</p>}
        <table>
          <thead><tr><th>Person</th><th>Email</th><th>State</th><th>Hub role</th><th>Credential</th><th>Entitled to</th><th>Last sign-in</th></tr></thead>
          <tbody>
            {(logins.data?.logins ?? []).map((l) => (
              <tr key={l.id}>
                <td>{l.identity.displayName}<div className="muted" style={{ fontSize: 11 }}>{l.identity.id}</div></td>
                <td>{l.email}</td>
                <td><Pill tone={l.identity.status === "active" && l.identity.lifecycleState !== "leaver" ? "ok" : "bad"}>{l.identity.status === "active" ? l.identity.lifecycleState : l.identity.status}</Pill></td>
                <td>
                  {/* What this person may administer HERE (owner-only to change, never one's own). Unrelated to the app roles on the right. */}
                  <select aria-label={`hub role of ${l.identity.displayName}`} style={{ ...input, padding: 4 }} value={l.identity.hubRole} onChange={(e) => void run(async () => {
                    await api(`/v1/sso/logins/${l.id}/hub-role`, { method: "POST", body: JSON.stringify({ hubRole: e.target.value }) });
                  })}>
                    {HUB_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                </td>
                <td>{l.lockedUntil && new Date(l.lockedUntil) > new Date() ? <Pill tone="warn">locked</Pill> : l.hasPasscode ? "passcode" : <Pill tone="dim">none</Pill>}</td>
                <td>{l.grants.length === 0 ? <span className="muted">nothing</span> : l.grants.map((g) => (
                  <div key={g.id} style={{ display: "flex", gap: 6, alignItems: "center" }}>
                    <code>{g.clientId}</code> as <strong>{g.appRole}</strong>{g.appTenant ? <span className="muted"> @ {g.appTenant}</span> : null}
                    <button style={{ ...btn, padding: "2px 8px", background: "transparent", border: "1px solid var(--border)", color: "var(--text)" }} onClick={() => void run(async () => { await api(`/v1/sso/grants/${g.id}`, { method: "DELETE" }); })}>Revoke</button>
                  </div>
                ))}</td>
                <td className="muted">{l.lastLoginAt ? new Date(l.lastLoginAt).toLocaleString() : "never"}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <h3 style={{ fontSize: 14, margin: "16px 0 6px" }}>Add a person</h3>
        <form style={{ display: "flex", gap: 8, flexWrap: "wrap" }} onSubmit={(e) => { e.preventDefault(); void run(async () => {
          await api("/v1/sso/logins", { method: "POST", body: JSON.stringify({ displayName: person.displayName, email: person.email, ...(person.passcode ? { passcode: person.passcode } : {}) }) });
          setPerson({ displayName: "", email: "", passcode: "" });
        }); }}>
          <input style={{ ...input, width: 180 }} placeholder="display name" value={person.displayName} onChange={(e) => setPerson({ ...person, displayName: e.target.value })} required />
          <input style={{ ...input, width: 220 }} placeholder="work email" type="email" value={person.email} onChange={(e) => setPerson({ ...person, email: e.target.value })} required />
          <input style={{ ...input, width: 200 }} placeholder="passcode (12+ chars)" type="password" autoComplete="new-password" value={person.passcode} onChange={(e) => setPerson({ ...person, passcode: e.target.value })} />
          <button style={btn} type="submit">Add person</button>
        </form>

        <h3 style={{ fontSize: 14, margin: "16px 0 6px" }}>Grant access to an application</h3>
        <form style={{ display: "flex", gap: 8, flexWrap: "wrap" }} onSubmit={(e) => { e.preventDefault(); void run(async () => {
          await api("/v1/sso/grants", { method: "POST", body: JSON.stringify({ ...grant, appTenant: grant.appTenant || null }) });
          setGrant({ identityId: "", clientId: "", appRole: "", appTenant: "" });
        }); }}>
          <select style={{ ...input, width: 220 }} value={grant.identityId} onChange={(e) => setGrant({ ...grant, identityId: e.target.value })} required>
            <option value="">person…</option>
            {(logins.data?.logins ?? []).map((l) => <option key={l.id} value={l.identity.id}>{l.identity.displayName} · {l.email}</option>)}
          </select>
          <select style={{ ...input, width: 180 }} value={grant.clientId} onChange={(e) => setGrant({ ...grant, clientId: e.target.value })} required>
            <option value="">application…</option>
            {(clients.data?.clients ?? []).map((c) => <option key={c.id} value={c.clientId}>{c.name}</option>)}
          </select>
          <input style={{ ...input, width: 140 }} placeholder="app role (e.g. ADMIN)" value={grant.appRole} onChange={(e) => setGrant({ ...grant, appRole: e.target.value })} required />
          <input style={{ ...input, width: 160 }} placeholder="app tenant (optional)" value={grant.appTenant} onChange={(e) => setGrant({ ...grant, appTenant: e.target.value })} />
          <button style={btn} type="submit">Grant access</button>
        </form>
      </Card>
    </>
  );
}
